import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { H3 } from 'foundations-components/transitional/components/H3';
import * as AssetUninstallableReason from 'marketplace-ui-assets-core/constants/AssetUninstallableReason';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import * as ThemeQueryField from 'marketplace-ui-assets-core/constants/ThemeQueryField';
import { formatCentsToDollars } from 'marketplace-ui-common/utils/currency';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { ResponsiveInnerSection } from 'marketplace-ui-common/layout/components/InnerSection';
import { getAssetBusinessTypeTranslationKey, getAssetPageTypeTranslationKey, getAssetPageFeatureTranslationKey } from 'marketplace-ui-assets-core/utils/i18n';
import { useAssetInstallability } from 'marketplace-ui-assets-core/hooks/useAssetInstallability';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import { GET_ASSET_CONTENTS } from '../../details/queries/assetContentsQuery';
import { getAppContextCoreType } from '../../selectors/app';
import { useMissingPortalScopes } from '../../hooks/useMissingPortalScopes';
import { selectPortalScopes, selectUserScopes } from '../../selectors/auth';
import AssetDetailsPage from '../../details/components/AssetDetailsPage';
import ExpandableTagsSection from '../../details/components/ExpandableTagsSection';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import ThemeClientExamples from './ThemeClientExamples';
import ThemeContents from './ThemeContents';
import ThemeContentsResponse from '../records/ThemeContentsResponse';
import ThemeRecommendationsSection from './ThemeRecommendationsSection';
/* @ts-expect-error module is untyped */
import UsageViewTracker from '../../components/tracking/UsageViewTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ThemeDetailsPage({
  themeDetails,
  use
}) {
  const isAdminPreview = use === 'admin-preview';
  const isPreview = use === 'preview' || isAdminPreview;
  const appContext = useSelector(getAppContextCoreType);
  const userScopes = useSelector(selectUserScopes);
  const portalScopes = useSelector(selectPortalScopes);
  const screen = isAdminPreview ? 'details admin preview' : use === 'preview' ? 'details preview' : 'details';
  const {
    businessTypes,
    clientExamples,
    offering: {
      homemade
    },
    offeringId,
    pageFeatures,
    pageTypes,
    pricing: {
      planType,
      cents
    },
    provider: {
      name: providerName
    },
    s3BucketKey,
    slug,
    tags
  } = themeDetails;
  const {
    assetUninstallableReason,
    requestState: assetInstallabilityRequestState
  } = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  const isFree = planType === PlanType.FREE;
  const [requestState, setRequestState] = useState(RequestState.UNINITIALIZED);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(GET_ASSET_CONTENTS, {
    variables: {
      bucketKey: s3BucketKey || ''
    }
  });
  const themeContentsRequestState = useMemo(() => {
    if (loading) {
      return RequestState.PENDING;
    } else if (error) {
      return RequestState.FAILED;
    } else if (data || previousData) {
      return RequestState.SUCCEEDED;
    } else {
      return RequestState.UNINITIALIZED;
    }
  }, [loading, data, previousData, error]);
  const themeContentsData = useMemo(() => {
    const contentsData = data || previousData;
    if (!contentsData) {
      return new ThemeContentsResponse();
    } else {
      return new ThemeContentsResponse(contentsData.contents);
    }
  }, [data, previousData]);
  const missingPortalScopes = useMissingPortalScopes(themeContentsData.scopes);
  const missingFeatures = useMemo(() => {
    return missingPortalScopes.map(scope => {
      const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}`;
      if (isValidI18nKey(scopeFeatureI18nKey)) {
        return I18n.text(scopeFeatureI18nKey, {
          locale: 'en'
        });
      }
      return null;
    });
  }, [missingPortalScopes]);
  const hasFeatures = useMemo(() => {
    const themeFeatures = [];
    const {
      contents,
      scopes
    } = themeContentsData;
    const hasSections = contents.some(contentItem => contentItem.fileType === 'TEMPLATE' && contentItem.templateType === 'section');
    if (hasSections) {
      themeFeatures.push('sections');
    }
    if (scopes) {
      scopes.forEach(scope => {
        const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}`;
        if (isValidI18nKey(scopeFeatureI18nKey)) {
          themeFeatures.push(I18n.text(scopeFeatureI18nKey, {
            locale: 'en'
          }));
        }
      });
    }
    return themeFeatures;
  }, [themeContentsData]);
  const getEventProperties = useCallback(() => {
    return {
      screen,
      slug,
      type: 'theme',
      industry: businessTypes,
      hasFeatures,
      homemade,
      offeringId,
      orderTotal: isFree ? 0 : formatCentsToDollars(cents),
      isInstallableByUser: assetUninstallableReason !== AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS,
      isNotInstallableReason: assetUninstallableReason || undefined,
      missingFeatures
    };
  }, [screen, slug, businessTypes, hasFeatures, homemade, offeringId, isFree, cents, assetUninstallableReason, missingFeatures]);

  /**
   * Usage tracking
   */
  const {
    setDefaultEventProperties,
    trackDetailsPageview,
    trackDetailsInteraction
  } = useContext(StorefrontUsageTrackerContext);

  // Set default event properties
  useEffect(() => {
    setDefaultEventProperties(getEventProperties());
  }, [getEventProperties, setDefaultEventProperties]);

  // Reset default event properties on unmount
  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]);
  useEffect(() => {
    if (isFulfilled(themeContentsRequestState) && isFulfilled(assetInstallabilityRequestState)) {
      setRequestState(RequestState.SUCCEEDED);
    }
  }, [themeContentsRequestState, assetInstallabilityRequestState]);
  const renderedThemeContents = useMemo(() => /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
    id: "contents",
    children: [/*#__PURE__*/_jsx(H3, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.contents.title"
      })
    }), /*#__PURE__*/_jsx(ThemeContents, {
      onTrackInteraction: trackDetailsInteraction,
      requestState: themeContentsRequestState,
      themeContents: themeContentsData
    })]
  }), [themeContentsData, themeContentsRequestState, trackDetailsInteraction]);
  const renderedClientExamples = useMemo(() => /*#__PURE__*/_jsx(Fragment, {
    children: !!clientExamples.length && /*#__PURE__*/_jsx(MainSection, {
      id: "client-showcase",
      color: "light",
      responsive: true,
      children: /*#__PURE__*/_jsxs(MainSectionInner, {
        children: [/*#__PURE__*/_jsx(H3, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.clientExamples.title"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.clientExamples.subtitle"
          })
        }), /*#__PURE__*/_jsx(ThemeClientExamples, {
          clientExamples: clientExamples,
          onTrackInteraction: trackDetailsInteraction
        })]
      })
    })
  }), [clientExamples, trackDetailsInteraction]);
  const renderedExpandableTagsSection = useMemo(() => /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("dt", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeInfo.pageTypes"
      })
    }), /*#__PURE__*/_jsx("dd", {
      children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
        isPreview: isPreview,
        onTrackInteraction: trackDetailsInteraction,
        queryField: "pageTypes",
        tags: pageTypes,
        getTagText: value => I18n.text(getAssetPageTypeTranslationKey(value))
      })
    }), /*#__PURE__*/_jsx("dt", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeInfo.pageFeatures"
      })
    }), /*#__PURE__*/_jsx("dd", {
      children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
        isPreview: isPreview,
        onTrackInteraction: trackDetailsInteraction,
        queryField: "pageFeatures",
        tags: pageFeatures,
        getTagText: value => I18n.text(getAssetPageFeatureTranslationKey(value))
      })
    }), /*#__PURE__*/_jsx("dt", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeInfo.businessTypes"
      })
    }), /*#__PURE__*/_jsx("dd", {
      children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
        isPreview: isPreview,
        onTrackInteraction: trackDetailsInteraction,
        queryField: ThemeQueryField.BUSINESS_TYPES,
        tags: businessTypes,
        getTagText: value => I18n.text(getAssetBusinessTypeTranslationKey(value))
      })
    }), tags.length > 0 && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.themeInfo.searchTags"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          isPreview: isPreview,
          onTrackInteraction: trackDetailsInteraction,
          queryField: ThemeQueryField.TAGS,
          tags: tags
        })
      })]
    })]
  }), [businessTypes, isPreview, pageFeatures, pageTypes, tags, trackDetailsInteraction]);
  const renderedThemeRecommendationsSection = useMemo(() => /*#__PURE__*/_jsx(ThemeRecommendationsSection, {
    businessTypes: businessTypes,
    isPreview: isPreview,
    offeringId: offeringId,
    onTrackInteraction: trackDetailsInteraction,
    onTrackPageview: trackDetailsPageview,
    providerName: providerName
  }), [businessTypes, isPreview, offeringId, providerName, trackDetailsInteraction, trackDetailsPageview]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "THEME_DETAILS_LOAD",
    children: [/*#__PURE__*/_jsx(AssetDetailsPage, {
      assetContentsRequestState: themeContentsRequestState,
      assetDetails: themeDetails,
      assetScopes: themeContentsData.scopes,
      offeringType: "THEME",
      renderedAssetContents: renderedThemeContents,
      renderedAssetRecommendationsSection: renderedThemeRecommendationsSection,
      renderedClientExamples: renderedClientExamples,
      renderedExpandableTagsSection: renderedExpandableTagsSection,
      use: use
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackDetailsPageview
    })]
  });
}